/**
 * The floor plan going wrong.
 *
 * A generator profile produces a building that makes sense: rooms are
 * rectangles, doors are where doors go, walls separate one thing from another
 * thing. That is exactly the problem. The Backrooms is unsettling because it is
 * mundane and wrong, and a clean partition of rectangles is only mundane — you
 * read it as an office floor once and then stop looking.
 *
 * This pass runs last, over the finished grid, and breaks that reading in ways a
 * builder could plausibly have done on purpose and then walked away from: a room
 * built inside a room with no way into it, a corridor that goes four metres and
 * stops, a wall with a doorway in every single cell so it is more gap than wall,
 * a column planted in the middle of an opening. None of it is spooky. All of it
 * is the sort of thing you would photograph and send to somebody.
 *
 * Two rules hold everywhere in here.
 *
 * **Connectivity survives.** `repairConnectivity` runs after this and would
 * tunnel to anything stranded, but a tunnel through solid rock is a scar, not a
 * plan — so every destructive edit is applied, checked and rolled back if it
 * left a single walkable cell cut off. The check is one flood fill; the pass as
 * a whole is a couple of milliseconds on a 140x140 grid.
 *
 * **The additive edits are free.** Punching a doorway or carving a slot can only
 * join things together, so those go in unguarded.
 */

import { Cell, type Grid, type Region, type RNG } from '../../core/types';
import {
  addRegion,
  fillRect,
  guardedEdit,
  inset,
  interiorRect,
  makeGuard,
  rectsOverlap,
  strokeRect,
  type ConnectivityGuard,
  type GenSite,
  type Rect,
} from './site';

/** Inclusive count ranges, one per operation. Zero disables it. */
export interface WrongnessProfile {
  /** Rooms built inside rooms: sealed, doored, or with a dead-end slot cut in. */
  innerRooms: readonly [number, number];
  /** Doorways that open onto solid rock. */
  doorsToNothing: readonly [number, number];
  /** Wall runs with an opening in every cell. */
  perforatedWalls: readonly [number, number];
  /** Columns planted in the middle of an opening. */
  pillarsInDoorways: readonly [number, number];
  /** Wall stubs growing into a room and stopping. */
  wallFins: readonly [number, number];
  /** Patches of column grid at a pitch nothing could be holding up. */
  tightPillarPatches: readonly [number, number];
  /** Confine every edit to this rect. Defaults to the whole interior. */
  bounds?: Rect;
  /**
   * Length of a wall fin in cells. Defaults to the two-to-four that reads at
   * room scale; a generator built out of thirty-metre spans wants longer ones,
   * or the fin is lost against the floor it is standing on.
   */
  finLength?: readonly [number, number];
  /**
   * Allow the inner room that leaves a one-cell ring corridor around itself.
   * Defaults on. Turn it off wherever the host regions are large open areas
   * rather than rooms: a ring around a forty-metre plate is not a strange
   * corridor, it is most of a district turned into solid rock.
   */
  ringRooms?: boolean;
}

/** Region kinds big and plain enough to build something wrong inside. */
const HOSTABLE = new Set(['room', 'atrium', 'pillar', 'hall', 'warren']);

/**
 * The smallest cell a column may stand in the middle of. Below this the two
 * gaps either side of a `Cell.Pillar` stop being a squeeze and start being a
 * wall: the free strip is 0.3 of a cell and the player is 0.64 m across.
 */
const MIN_PILLAR_CELL = 2.9;

/** Working state, so the guard's buffers are allocated once for the whole pass. */
interface Wrong {
  site: GenSite;
  grid: Grid;
  rng: RNG;
  bounds: Rect;
  guard: ConnectivityGuard;
  finLength: readonly [number, number];
  ringRooms: boolean;
}

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/** Default fin length in cells, tuned against a room rather than a floor plate. */
const FIN_LENGTH: readonly [number, number] = [2, 4];

/**
 * Make the building stop making sense. Safe to call from any generator profile,
 * as the last thing it does.
 */
export function applyWrongness(site: GenSite, profile: WrongnessProfile): void {
  const { grid, rng } = site;
  const w: Wrong = {
    site,
    grid,
    rng,
    bounds: profile.bounds ?? interiorRect(grid),
    guard: makeGuard(grid),
    finLength: profile.finLength ?? FIN_LENGTH,
    ringRooms: profile.ringRooms ?? true,
  };

  // Order matters only in that the destructive edits want first pick of the
  // open floor: a room with a wall fin already in it is a worse host for an
  // inner room than one without.
  innerRooms(w, pick(rng, profile.innerRooms));
  wallFins(w, pick(rng, profile.wallFins));
  tightPillarPatches(w, pick(rng, profile.tightPillarPatches));
  perforatedWalls(w, pick(rng, profile.perforatedWalls));
  doorsToNothing(w, pick(rng, profile.doorsToNothing));
  pillarsInDoorways(w, pick(rng, profile.pillarsInDoorways));
}

function pick(rng: RNG, range: readonly [number, number]): number {
  return range[1] <= 0 ? 0 : rng.int(range[0], range[1]);
}

/** Every destructive edit in here goes through the shared reachability guard. */
function tryEdit(w: Wrong, edit: () => void): boolean {
  return guardedEdit(w.guard, edit);
}

/* ------------------------------------------------------------------ *
 * Rooms inside rooms
 * ------------------------------------------------------------------ */

/**
 * A second, smaller room built inside a room.
 *
 * Three ways it can go, and all three are worth having. Sealed is the best of
 * them: a blank plastered box standing in the middle of a room with no way into
 * it, which the player will walk all the way around looking for a door. Set so
 * that it leaves exactly one cell of floor all round, it also gives the level a
 * ring corridor that closes on itself with nothing inside — which is the same
 * fact stated from the other side.
 *
 * The doored variant is a room you can get into, which is more ordinary and
 * therefore fine to have more of. The slot is a corridor that runs four metres
 * into the block and stops.
 */
function innerRooms(w: Wrong, count: number): void {
  if (count <= 0) return;
  const { site, rng } = w;
  const rooms = site.regions.filter(
    (r) => HOSTABLE.has(r.kind) && r.w >= 8 && r.h >= 8 && insideBounds(w, r),
  );
  if (rooms.length === 0) return;
  rng.shuffle(rooms);

  // Long corridors are the only thing on a level like the Lobby a player can
  // navigate by, and one of them runs straight through plenty of these rooms.
  // Walling a block across it would cost more than the block is worth.
  const halls = site.regions.filter((r) => r.kind === 'hall');

  let done = 0;
  for (const room of rooms) {
    if (done >= count) break;
    const rect = innerRect(w, room);
    if (!rect) continue;
    if (halls.some((hall) => rectsOverlap(rect, hall, 1))) continue;
    if (!clearFloor(w, rect, 1)) continue;

    const roll = rng.next();
    const doored = roll < 0.3;
    const slotted = !doored && roll < 0.48;
    // A doorway needs somewhere to lead, so a doored block has to be hollow.
    if ((doored || slotted) && (rect.w < 3 || rect.h < 3)) continue;

    const side = rng.int(0, 3);
    const slotDepth = Math.max(1, Math.min(rect.w, rect.h) - 2);
    const ok = tryEdit(w, () => {
      if (doored) {
        strokeRect(w.grid, rect, Cell.Wall);
        fillRect(w.grid, inset(rect, 1), Cell.Floor);
        punchSide(w, rect, side);
        return;
      }
      fillRect(w.grid, rect, Cell.Wall);
      if (slotted) carveSlot(w, rect, side, slotDepth);
    });
    if (!ok) continue;

    // Label the inside so supplies know it is worth hiding something in.
    if (doored) addRegion(site, inset(rect, 1), 'closet');
    done++;
  }
}

/** Where inside a room to put the block, and how big. */
function innerRect(w: Wrong, room: Region): Rect | null {
  const { rng } = w;
  // A one-cell margin turns the room into a ring corridor; two leaves a room
  // with a lump in it. The ring is the stranger of the two, so it is not rare —
  // but only in a room you can see across, or it stops being a ring and becomes
  // a large amount of the level that is now solid.
  const ringable = w.ringRooms && room.w <= 16 && room.h <= 16;
  const margin = ringable && rng.chance(0.36) ? 1 : 2;
  const maxW = room.w - 2 * margin;
  const maxH = room.h - 2 * margin;
  if (maxW < 3 || maxH < 3) return null;
  if (margin === 1) return { x: room.x + 1, y: room.y + 1, w: maxW, h: maxH };
  const bw = rng.int(3, Math.min(maxW, 6));
  const bh = rng.int(3, Math.min(maxH, 6));
  return {
    x: rng.int(room.x + margin, room.x + room.w - margin - bw),
    y: rng.int(room.y + margin, room.y + room.h - margin - bh),
    w: bw,
    h: bh,
  };
}

/** One opening in the middle of one side of a hollow block. */
function punchSide(w: Wrong, rect: Rect, side: number): void {
  const { grid } = w;
  const mx = rect.x + (rect.w >> 1);
  const my = rect.y + (rect.h >> 1);
  if (side === 0) grid.set(mx, rect.y, Cell.Doorway);
  else if (side === 1) grid.set(mx, rect.y + rect.h - 1, Cell.Doorway);
  else if (side === 2) grid.set(rect.x, my, Cell.Doorway);
  else grid.set(rect.x + rect.w - 1, my, Cell.Doorway);
}

/** A one-cell corridor cut into a solid block, stopping short of the far side. */
function carveSlot(w: Wrong, rect: Rect, side: number, depth: number): void {
  const { grid } = w;
  const mx = rect.x + (rect.w >> 1);
  const my = rect.y + (rect.h >> 1);
  for (let k = 0; k < depth; k++) {
    if (side === 0) grid.set(mx, rect.y + k, k === 0 ? Cell.Doorway : Cell.Floor);
    else if (side === 1) grid.set(mx, rect.y + rect.h - 1 - k, k === 0 ? Cell.Doorway : Cell.Floor);
    else if (side === 2) grid.set(rect.x + k, my, k === 0 ? Cell.Doorway : Cell.Floor);
    else grid.set(rect.x + rect.w - 1 - k, my, k === 0 ? Cell.Doorway : Cell.Floor);
  }
}

/* ------------------------------------------------------------------ *
 * Walls that stop
 * ------------------------------------------------------------------ */

/**
 * A wall growing out of a wall and stopping in the middle of the room. The grid
 * version of the same idea the `stub-wall` prop covers at sub-cell scale: this
 * one is a real full-height wall the renderer builds, so it casts, occludes and
 * hides things behind it, and it changes the shape of the room rather than
 * standing in it.
 */
function wallFins(w: Wrong, count: number): void {
  if (count <= 0) return;
  const { grid, rng } = w;
  let done = 0;
  for (let tries = 0; tries < count * 40 && done < count; tries++) {
    const x = rng.int(w.bounds.x + 1, w.bounds.x + w.bounds.w - 2);
    const y = rng.int(w.bounds.y + 1, w.bounds.y + w.bounds.h - 2);
    if (grid.at(x, y) !== Cell.Floor) continue;

    // Grows out of a wall, so it reads as construction rather than as debris.
    let anchor = -1;
    for (let d = 0; d < 4; d++) {
      if (grid.isSolid(x + DX[d]!, y + DY[d]!)) {
        anchor = d;
        break;
      }
    }
    if (anchor < 0) continue;
    const dir = anchor ^ 1; // the opposite direction: into the room
    const len = rng.int(w.finLength[0], w.finLength[1]);

    const cells: number[] = [];
    let ok = true;
    for (let k = 0; k < len && ok; k++) {
      const cx = x + DX[dir]! * k;
      const cy = y + DY[dir]! * k;
      if (!insideBoundsCell(w, cx, cy)) ok = false;
      else if (grid.at(cx, cy) !== Cell.Floor) ok = false;
      else if (touchesDoorway(w, cx, cy)) ok = false;
      else cells.push(cy * grid.w + cx);
    }
    // The end has to be in mid-air, or it is not a stub, it is a partition.
    if (!ok || !grid.isWalkable(x + DX[dir]! * len, y + DY[dir]! * len)) continue;
    const built = tryEdit(w, () => {
      for (const i of cells) grid.cells[i] = Cell.Wall;
    });
    if (built) done++;
  }
}

/* ------------------------------------------------------------------ *
 * Openings
 * ------------------------------------------------------------------ */

/**
 * A wall with an opening in every single cell of it, so what is left is a row
 * of frames with nothing between them. It joins two rooms that were already
 * joined, which is what makes it pointless, and it is legible from right across
 * the floor, which is what makes it worth doing.
 */
function perforatedWalls(w: Wrong, count: number): void {
  if (count <= 0) return;
  const { grid, rng } = w;
  let done = 0;
  for (let tries = 0; tries < count * 60 && done < count; tries++) {
    const x = rng.int(w.bounds.x + 2, w.bounds.x + w.bounds.w - 3);
    const y = rng.int(w.bounds.y + 2, w.bounds.y + w.bounds.h - 3);
    if (grid.at(x, y) !== Cell.Wall) continue;
    const vertical = grid.isWalkable(x - 1, y) && grid.isWalkable(x + 1, y);
    if (!vertical && !(grid.isWalkable(x, y - 1) && grid.isWalkable(x, y + 1))) continue;

    const sx = vertical ? 0 : 1;
    const sy = vertical ? 1 : 0;
    const run: number[] = [];
    for (const sign of [1, -1]) {
      for (let k = sign === 1 ? 0 : 1; k < 6; k++) {
        const cx = x + sx * sign * k;
        const cy = y + sy * sign * k;
        if (!insideBoundsCell(w, cx, cy)) break;
        if (grid.at(cx, cy) !== Cell.Wall) break;
        const open = vertical
          ? grid.isWalkable(cx - 1, cy) && grid.isWalkable(cx + 1, cy)
          : grid.isWalkable(cx, cy - 1) && grid.isWalkable(cx, cy + 1);
        if (!open) break;
        run.push(cy * grid.w + cx);
      }
    }
    if (run.length < 5) continue;
    for (const i of run) grid.cells[i] = Cell.Doorway;
    done++;
  }
}

/**
 * A doorway with the building on the other side of it. Additive — it only ever
 * turns a wall cell into somewhere you can stand — so it needs no guard, and
 * what it costs the player is one wrong turn into an alcove the size of a door.
 */
function doorsToNothing(w: Wrong, count: number): void {
  if (count <= 0) return;
  const { grid, rng } = w;
  let done = 0;
  for (let tries = 0; tries < count * 80 && done < count; tries++) {
    const x = rng.int(w.bounds.x + 2, w.bounds.x + w.bounds.w - 3);
    const y = rng.int(w.bounds.y + 2, w.bounds.y + w.bounds.h - 3);
    if (grid.at(x, y) !== Cell.Wall) continue;

    // Exactly one way in, and rock two cells deep behind it.
    let from = -1;
    let ways = 0;
    for (let d = 0; d < 4; d++) {
      if (!grid.isWalkable(x + DX[d]!, y + DY[d]!)) continue;
      ways++;
      from = d;
    }
    if (ways !== 1) continue;
    const c = grid.at(x + DX[from]!, y + DY[from]!);
    if (c !== Cell.Floor && c !== Cell.Damp) continue;
    const back = from ^ 1;
    if (!grid.isSolid(x + DX[back]! * 1, y + DY[back]! * 1)) continue;
    if (!grid.isSolid(x + DX[back]! * 2, y + DY[back]! * 2)) continue;

    grid.set(x, y, Cell.Doorway);
    done++;
  }
}

/**
 * A column planted in the middle of an opening. It is passable — a `Cell.Pillar`
 * leaves three tenths of a cell clear either side — and it is the single
 * cheapest thing in this file, because the player has to sidestep something
 * that has no business being there and then keep walking.
 */
function pillarsInDoorways(w: Wrong, count: number): void {
  if (count <= 0) return;
  const { grid, rng } = w;
  if (grid.cellSize < MIN_PILLAR_CELL) return;
  const doors: number[] = [];
  for (let y = w.bounds.y; y < w.bounds.y + w.bounds.h; y++) {
    for (let x = w.bounds.x; x < w.bounds.x + w.bounds.w; x++) {
      if (grid.at(x, y) === Cell.Doorway) doors.push(y * grid.w + x);
    }
  }
  if (doors.length === 0) return;
  rng.shuffle(doors);
  for (let n = 0; n < count && n < doors.length; n++) grid.cells[doors[n]!] = Cell.Pillar;
}

/**
 * Structural columns at a pitch that could not be holding anything up: every
 * other cell, in a patch, in the middle of a room that has a perfectly ordinary
 * column grid ten metres away. Passable in every direction — the free cells
 * form a connected lattice — so this is additive too.
 */
function tightPillarPatches(w: Wrong, count: number): void {
  if (count <= 0) return;
  const { site, grid, rng } = w;
  const rooms = site.regions.filter(
    (r) => HOSTABLE.has(r.kind) && r.w >= 9 && r.h >= 9 && insideBounds(w, r),
  );
  if (rooms.length === 0) return;
  rng.shuffle(rooms);

  let done = 0;
  for (const room of rooms) {
    if (done >= count) break;
    const pw = rng.int(4, Math.min(8, room.w - 4));
    const ph = rng.int(4, Math.min(8, room.h - 4));
    if (pw < 4 || ph < 4) continue;
    const rect: Rect = {
      x: rng.int(room.x + 2, room.x + room.w - 2 - pw),
      y: rng.int(room.y + 2, room.y + room.h - 2 - ph),
      w: pw,
      h: ph,
    };
    // Two cells of clearance from any threshold: a column grid across the mouth
    // of a door is a blocked door, however passable the arithmetic says it is.
    if (!clearFloor(w, rect, 2)) continue;
    for (let y = rect.y; y < rect.y + rect.h; y += 2) {
      for (let x = rect.x; x < rect.x + rect.w; x += 2) {
        if (grid.at(x, y) === Cell.Floor) grid.set(x, y, Cell.Pillar);
      }
    }
    done++;
  }
}

/* ------------------------------------------------------------------ *
 * Shared tests
 * ------------------------------------------------------------------ */

/** True when every cell of `r`, grown by `pad`, is plain walkable floor. */
function clearFloor(w: Wrong, r: Rect, pad: number): boolean {
  const { grid } = w;
  for (let y = r.y - pad; y < r.y + r.h + pad; y++) {
    for (let x = r.x - pad; x < r.x + r.w + pad; x++) {
      if (!insideBoundsCell(w, x, y)) return false;
      const c = grid.at(x, y);
      if (c !== Cell.Floor && c !== Cell.Damp) return false;
    }
  }
  return true;
}

function touchesDoorway(w: Wrong, x: number, y: number): boolean {
  const { grid } = w;
  if (grid.at(x, y) === Cell.Doorway) return true;
  for (let d = 0; d < 4; d++) if (grid.at(x + DX[d]!, y + DY[d]!) === Cell.Doorway) return true;
  return false;
}

function insideBounds(w: Wrong, r: { x: number; y: number; w: number; h: number }): boolean {
  const b = w.bounds;
  return r.x >= b.x && r.y >= b.y && r.x + r.w <= b.x + b.w && r.y + r.h <= b.y + b.h;
}

function insideBoundsCell(w: Wrong, x: number, y: number): boolean {
  const b = w.bounds;
  return x >= b.x && y >= b.y && x < b.x + b.w && y < b.y + b.h;
}
